/**
 * this component can be accessed from the client menu
 */
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  NgZone
} from "@angular/core";
import {
  SourceAnswerService,
  SourceUploadProcessStateResult,
  SourceService,
  Source,
  ListOfSource,
  TaxReturnSubType,
  TaxReturnType,
  TaxReturnSourceFileService,
  ListOfTaxReturnSourceFiles
} from "../../../swaggerServices";
import { ClientNotificationService } from "../client-notification.service";
import { Subscription, Observable, of, Subject } from "rxjs";
import { TaxReturnsResolver } from "../tax-returns/tax-returns-resolver";
import { ActivatedRoute } from "@angular/router";
import { ConfigStateService } from "../../app/config/config-state.service";
import moment from "moment";
import { ProcessStatePollingService } from "../../shared/services/process-state-polling.service";
import { ProcessStates } from "../../shared/types/processState";
import * as XLSX from "xlsx";
import { takeUntil } from "rxjs/operators";
import { TranslocoService} from "@jsverse/transloco";

declare var $: any;

interface ISourceUploadProcessStateResult
  extends SourceUploadProcessStateResult {
  message?: string;
  error?: { message?: string };
}

@Component({
    selector: "app-dynamic-sources",
    templateUrl: "./dynamic-sources.component.html",
    styleUrls: ["./dynamic-sources.component.scss"],
    standalone: false
})
export class DynamicSourcesComponent implements OnInit, OnDestroy {
  bulkAddSourceAnswer?: ISourceUploadProcessStateResult;
  subscriptions: Subscription[] = [];
  sourceId?: number;
  atlasId?: number; // only available when accessed from tax return page
  assigneeId?: number; // only available when accessed from tax return page
  enableSourceFileListUpload?: boolean | null = null; // only available when accessed from tax return page
  sourceFile?: File;
  baseSourceList: ListOfSource;
  taxReturnSubType = 0;
  taxYr: number;
  sourceFiles: ListOfTaxReturnSourceFiles;
  sourceFileNameMap = {};
  isDroppingFiles: boolean = false;
  uploadedXlsxFiles: {
    file: File;
    source: Source;
    uploadStatus?: string;
    message?: string;
  }[] = [];
  isProcessingFiles: boolean = false;
  isUploadingFilesCompleted: boolean = false;
  uploadListModalContent: string = "Processing Files";
  uploadError: boolean = false;
  uploadListErrorMsg: string = "";
  private unsubscribe: Subject<void> = new Subject();
  showUploadSourceResult: boolean = false;
  // transloco text
  filesAreBeingUploadedText: string;
  errorsOccurredDuringProcessText: string;
  sourceUploadedSuccessfully: string;
  clickOkToViewDetailsText: string;
  areYouSureYouWantToProceedText: string;
  interruptedUploadProcess: boolean = false;

  @ViewChild("uploadListModal") uploadListModal?: ElementRef;
  @ViewChild("uploadListErrorModal") uploadListErrorModal?: ElementRef;
  @ViewChild("deleteFilePopup") deleteFilePopup?: ElementRef;
  @ViewChild("interruptUploadModal") interruptUploadModal?: ElementRef;

  constructor(
    private sourceAnswerService: SourceAnswerService,
    private sourceService: SourceService,
    private taxReturnSourceFileService: TaxReturnSourceFileService,
    private taxReturnsResolver: TaxReturnsResolver,
    private activatedRoute: ActivatedRoute,
    public config: ConfigStateService,
    private processStatePollingService: ProcessStatePollingService,
    private clientNotificationService: ClientNotificationService,
    private zone: NgZone,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.taxReturnSourceFileService
        .getTaxReturnSourceFilesByTaxReturn(
          Number(this.activatedRoute.snapshot.paramMap.get("id"))
        )
        .subscribe((l: ListOfTaxReturnSourceFiles) => {
          this.sourceFileNameMap = l.reduce((source, item) => {
            source[item.sourceId] = {
              id: item.id,
              fileName: item.fileName,
              updatedAt: moment(item.updatedAt)
                .format("MMM Do YYYY h:mm:ss A")
                .toString()
            };
            return source;
          }, []);
        })
    );

    this.subscriptions.push(
      this.sourceService.getSources().subscribe((d: ListOfSource) => {
        this.baseSourceList = d.filter(s => s.requireAtlasId && !s.hidden);
      })
    );

    this.subscriptions.push(
      this.activatedRoute.queryParams.subscribe(params => {
        if (params.taxYear) this.taxYr = params.taxYear;
      })
    );

    // pull in translations using transloco service
    this.translocoService
      .selectTranslate("files-are-being-uploaded")
      .subscribe(value => (this.filesAreBeingUploadedText = value));

    this.translocoService
      .selectTranslate("errors-occurred-during-process")
      .subscribe(value => (this.errorsOccurredDuringProcessText = value));

    this.translocoService
      .selectTranslate("source-upload-success")
      .subscribe(value => (this.sourceUploadedSuccessfully = value));

    this.translocoService
      .selectTranslate("click-ok-to-view-details")
      .subscribe(value => (this.clickOkToViewDetailsText = value));

    this.interruptedUploadProcess = false;
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  updateTaxReturnSourceFiles() {
    this.taxReturnSourceFileService
      .getTaxReturnSourceFilesByTaxReturn(
        Number(this.activatedRoute.snapshot.paramMap.get("id"))
      )
      .subscribe((l: ListOfTaxReturnSourceFiles) => {
        this.sourceFileNameMap = l.reduce((source, item) => {
          source[item.sourceId] = {
            id: item.id,
            fileName: item.fileName,
            updatedAt: moment(item.updatedAt)
              .format("MMM Do YYYY h:mm:ss A")
              .toString()
          };
          return source;
        }, this.sourceFileNameMap);
      });
  }

  onFileOver() {
    this.isDroppingFiles = true;
  }

  onFileDragLeave() {
    this.isDroppingFiles = false;
  }

  async onFilesDropped(files: FileSystemEntry[]) {
    this.isDroppingFiles = false;
    this.isProcessingFiles = true;
    this.isUploadingFilesCompleted = false;
    this.uploadError = false;
    this.uploadListErrorMsg = "";
    this.uploadedXlsxFiles = [];

    $(this.uploadListModal.nativeElement).modal("show");

    const fileProcessingPromises = files.map(entry => this.processEntry(entry));

    try {
      await Promise.all(fileProcessingPromises);
    } catch (error) {
      this.isProcessingFiles = false;
      this.uploadError = true;
      this.isUploadingFilesCompleted = true;
      this.uploadListErrorMsg = error.message;
      return;
    }

    if (this.uploadedXlsxFiles.length === 0) {
      this.isUploadingFilesCompleted = true;
      this.isProcessingFiles = false;
      this.uploadError = true;
      this.uploadListErrorMsg = "No valid XLSX files found for uploading";
      return;
    }

    const taxYr = this.activatedRoute.queryParams["value"].taxYear;
    const clientId = this.activatedRoute.queryParams["value"].clientId;
    const taxReturn = await this.taxReturnsResolver.taxReturn;

    let uploadCounter = 0;
    let errorFlag: boolean = false;

    this.uploadListModalContent = this.filesAreBeingUploadedText;
    for(const file of this.uploadedXlsxFiles) {
      if (!file.uploadStatus) {
        this.sourceAnswerService
          .bulkAddSourceAnswer(
            file.file,
            taxYr,
            file.source.id,
            taxReturn.atlasId,
            taxReturn.assigneeId,
            clientId,
            taxReturn.taxReturnSubTypeId,
            "replace"
          )
          .subscribe(
            () => {
              this.processStatePollingService.startPolling(
                `uploadSource_${taxYr}_${file.source.id}`,
                "assignee",
                taxReturn.assigneeId,
                3000,
                3
              );
            },
            err => {
              file.uploadStatus = "F";
              file.message = err.error.message;
              return;
            }
          );

        await new Promise<void> ((resolve, reject) => {
          this.processStatePollingService
          .subscribeData(
            `uploadSource_${taxYr}_${file.source.id}`,
            "assignee",
            taxReturn.assigneeId
          )
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(
            result => {
              if (!result || !result.state || !result.message) {
                return;
              }
              if (
                result.state === ProcessStates.CANCELED ||
                result.state === ProcessStates.FAILED ||
                result.state === ProcessStates.SUCCESS
              ) {
                uploadCounter++;
                file.uploadStatus = result.state;
                file.message = result.message;
                this.processStatePollingService.cancelPolling(
                  `uploadSource_${taxYr}_${file.source.id}`,
                  "assignee",
                  taxReturn.assigneeId
                );
                if (
                  result.state === ProcessStates.CANCELED ||
                  result.state === ProcessStates.FAILED
                ) {
                  errorFlag = true;
                }
                resolve()
              }
            },
            err => {
              file.uploadStatus = "F";
              file.message = err.error.message;
              reject();
            }
          );
        });
      } else {
        uploadCounter++;
        errorFlag = true;
      }
    };

    this.isProcessingFiles = false;
    this.isUploadingFilesCompleted = true;

    if(!this.interruptedUploadProcess) {
      $(this.uploadListModal.nativeElement).modal("show");
      this.uploadListModalContent = "";
      if (errorFlag) {
        this.uploadListModalContent = this.errorsOccurredDuringProcessText;
      } else {
        this.uploadListModalContent = this.sourceUploadedSuccessfully
      }
      this.uploadListModalContent += "\n" + this.clickOkToViewDetailsText;
      this.updateTaxReturnSourceFiles();
    }
  }

  async processEntry(entry: FileSystemEntry) {
    if (entry.isFile) {
      // The `isFile` property doesn't narrow the type, so an assertion is needed here
      const fileEntry = entry as unknown as FileSystemFileEntry;
      await new Promise<void | Error>((resolve, reject) => {
        fileEntry.file(async file => {
          try {
            await this.processFile(file);
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      });
    } else if (entry.isDirectory){
      // The `isDirctory` property doesn't narrow the type, so an assertion is needed here
      const directoryEntry = entry as unknown as FileSystemDirectoryEntry;
      const reader = directoryEntry.createReader();
      await new Promise<void | Error>((resolve, reject) => {
        reader.readEntries(async entries => {
          try {
            const processingPromises = entries.map(entry =>
              this.processEntry(entry)
            );
            await Promise.all(processingPromises);
            resolve(undefined);
          } catch (error) {
            reject(error);
          }
        });
      });
    }
  }

  processFile(file: File) {
    return new Promise<void | Error>((resolve, reject) => {
      try {
        if (file.name.endsWith(".xlsx")) {
          const reader = new FileReader();
          reader.onload = (event: any) => {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, {
              type: "array",
              sheetRows: 1 //One row only
            });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const a1CellValue = firstSheet["A1"]?.v || null;
            if (a1CellValue) {
              const source = this.baseSourceList.find(
                obj => obj.name.toLowerCase() === String(a1CellValue).toLowerCase()
              );
              if (source) {
                const alreadyUploaded = this.uploadedXlsxFiles.find(
                  obj => obj.source.id === source.id
                );
                if (alreadyUploaded) {
                  this.uploadedXlsxFiles.push({
                    file: file,
                    source: source,
                    uploadStatus: "F",
                    message: "One file already found for this source."
                  });
                } else {
                  this.uploadedXlsxFiles.push({ file: file, source: source });
                }
              }
            }
            resolve();
          };
          reader.onerror = reject;
          reader.readAsArrayBuffer(file);
        } else {
          throw new Error("Only .xlsx files are accepted for source upload");
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  toggleShowUploadSourceResult() {
    if (this.isUploadingFilesCompleted && !this.uploadError) {
      if (this.showUploadSourceResult) {
        this.uploadedXlsxFiles = [];
      }
      this.showUploadSourceResult = !this.showUploadSourceResult;
    }
  }

  showUploadErrorPopup(index) {
    let message = this.uploadedXlsxFiles[index].message;
    if (message.startsWith("uploadSource")) {
      this.uploadListErrorMsg = message.split("message: ")[1];
    } else {
      this.uploadListErrorMsg = message;
    }
    $(this.uploadListErrorModal.nativeElement).modal("show");
  }

  showDeleteFilePopup(sourceFileId: number) {
    $(this.deleteFilePopup.nativeElement)
      .modal({
        onApprove: async () => {
          this.zone.run(() => {
            $(this.deleteFilePopup.nativeElement).modal("hide");
            this.clientNotificationService.appendMessage(
              "Source File Deletion Process",
              "Source file deletion process started",
              true
            );

            try {
              this.taxReturnSourceFileService
                .deleteTaxReturnSourceFileById(sourceFileId)
                .subscribe(
                  res => {
                    this.updateTaxReturnSourceFiles();
                    this.clientNotificationService.appendMessage(
                      "Source File Deletion Completed",
                      res.message,
                      true
                    );
                  },
                  err => {
                    this.updateTaxReturnSourceFiles();
                    this.clientNotificationService.appendMessage(
                      "Source File Deletion Failed",
                      err.message,
                      false
                    );
                  }
                );
            } catch (err) {
              this.updateTaxReturnSourceFiles();
              this.clientNotificationService.appendMessage(
                "Source File Deletion Failed",
                err.message,
                false
              );
            }
          });
        }
      })
      .modal("show");
  }

  canDeactivate(): Observable<boolean> {
    if (!this.isProcessingFiles) {
      return of(true);
    } else {
      const result = new Subject<boolean>();

      $(this.interruptUploadModal.nativeElement).modal({
        onApprove: () => {
          this.subscriptions.forEach(s => s.unsubscribe());
          this.interruptedUploadProcess = true;
          result.next(true);
          result.complete();
        },
        onDeny: () => {
          result.next(false);
          result.complete();
        }
      }).modal("show");

      return result.asObservable();
    }
  }
}
